import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography, Divider, TextField, Tooltip, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useStyles from './styles';

const coefficients = [
    { name: 'coefProduitATC', label: 'PRODUIT ATC', defaultValue: 1.4 },
    { name: 'coefCreation', label: 'CRÉATION', defaultValue: 1.6 },
    { name: 'coefSAV', label: 'SAV', defaultValue: 1.8 },
];

const TotalBlocks = ({
    coeffs,
    handleCoeffChange,
    totalRevient,
    coefTresSerre,
    totalTresSerre,
    totalNormal,
    fsPrice,
    onSelectPrice,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleCoeffChangeWithPrice = useCallback((e) => {
        handleCoeffChange(e);
        const coef = coefficients.find(c => c.name === e.target.name);
        if (coef && onSelectPrice) {
            const value = parseFloat(e.target.value) || 1;
            const price = totalRevient * value + fsPrice;
            onSelectPrice(Math.ceil(price / 10) * 10);
        }
    }, [handleCoeffChange, totalRevient, fsPrice, onSelectPrice]);

    const renderCoefBlock = useCallback((coef) => {
        const value = coeffs[coef.name] ?? coef.defaultValue;
        const price = totalRevient * parseFloat(value || 1) + fsPrice;
        const roundedPrice = Math.ceil(price / 10) * 10;

        const title = `Calcul : (Prix de Revient x Coef ${coef.label}) + Coût Façon. Le prix est arrondi au 10 supérieur.`;

        return (
            <Box key={coef.name} display="flex" flexDirection="column" alignItems="center" flexGrow={1}
                onClick={() => onSelectPrice && onSelectPrice(roundedPrice)}
                style={{ cursor: onSelectPrice ? 'pointer' : 'default', borderRadius: 8, padding: 4 }}
                className={onSelectPrice ? classes.selectableRow : ''}
            >
                <Tooltip title={title}>
                    <Typography variant="h6" className={classes.totalText}>{coef.label}</Typography>
                </Tooltip>
                <TextField
                    label="Coef"
                    type="number"
                    name={coef.name}
                    value={value}
                    onChange={handleCoeffChangeWithPrice}
                    onClick={e => e.stopPropagation()}
                    variant="outlined"
                    size="small"
                    style={{ width: 80, margin: '4px' }}
                    inputProps={{ step: 0.1 }}
                />
                <Box mt="auto" display="flex" alignItems="flex-end" height={40}>
                    <Typography className={classes.finalPrice}>{roundedPrice} €</Typography>
                </Box>
            </Box>
        );
    }, [coeffs, handleCoeffChangeWithPrice, totalRevient, fsPrice, classes, onSelectPrice]);

    return (
        <Box>
            <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} className={classes.totalText}>
                    TOTAL PRIX REVIENT : {(totalRevient + fsPrice).toFixed(0)} €
                </Typography>
                <Divider orientation="horizontal" flexItem />
            </Box>

            <Grid container spacing={2} alignItems="stretch" style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
                {coefficients.map((coef, idx) => (
                    <React.Fragment key={coef.name}>
                        <Grid item xs={6} sm style={{ flex: isMobile ? undefined : 1, display: 'flex', flexDirection: 'column' }}>
                            {renderCoefBlock(coef)}
                        </Grid>
                        {!isMobile && idx < coefficients.length - 1 && (
                            <Divider orientation="vertical" flexItem className={classes.verticalDivider} />
                        )}
                    </React.Fragment>
                ))}
                {!isMobile && <Divider orientation="vertical" flexItem className={classes.verticalDivider} />}
                <Grid item xs={6} sm style={{ flex: isMobile ? undefined : 1, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" flexDirection="column" flexGrow={1} alignItems="center"
                        onClick={() => onSelectPrice && onSelectPrice(Math.ceil(totalTresSerre / 10) * 10)}
                        style={{ cursor: onSelectPrice ? 'pointer' : 'default', borderRadius: 8, padding: 4 }}
                        className={onSelectPrice ? classes.selectableRow : ''}
                    >
                        <Box textAlign="center">
                            <Tooltip title={`Si le prix de revient est < 3000€ le coef est de 1.25, sinon 1.27. Prix arrondi au 10 supérieur.`}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} className={classes.totalText}>TRÈS SERRÉ</Typography>
                            </Tooltip>
                            <Typography variant="body2" color="textSecondary" align="center">
                                (coef: {coefTresSerre.toFixed(2)})
                            </Typography>
                        </Box>
                        <Box mt="auto" display="flex" alignItems="flex-end" height={40}>
                            <Typography className={classes.finalPrice}>{(totalTresSerre).toFixed(0)} €</Typography>
                        </Box>
                    </Box>
                </Grid>
                {!isMobile && <Divider orientation="vertical" flexItem className={classes.verticalDivider} />}
                <Grid item xs={12} sm style={{ flex: isMobile ? undefined : 1, display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" flexDirection="column" flexGrow={1} alignItems="center"
                        onClick={() => onSelectPrice && onSelectPrice(totalNormal)}
                        style={{ cursor: onSelectPrice ? 'pointer' : 'default', borderRadius: 8, padding: 4 }}
                        className={onSelectPrice ? classes.selectableRow : ''}
                    >
                        <Box textAlign="center">
                            <Tooltip title={`Calcul : [(Coût MFS - Façon) x Coef MFS + Prix Pierres avec coef] x 1.08 + 20 + Coût Façon. Le prix est arrondi au 10 supérieur.`}>
                                <Typography variant={isMobile ? "subtitle1" : "h6"} className={classes.totalText}>NORMAL</Typography>
                            </Tooltip>
                        </Box>
                        <Box mt="auto" display="flex" alignItems="flex-end" height={40}>
                            <Typography className={classes.finalPrice}>{(totalNormal).toFixed(0)} €</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

TotalBlocks.propTypes = {
    coeffs: PropTypes.object.isRequired,
    handleCoeffChange: PropTypes.func.isRequired,
    totalRevient: PropTypes.number.isRequired,
    coefTresSerre: PropTypes.number.isRequired,
    totalTresSerre: PropTypes.number.isRequired,
    totalNormal: PropTypes.number.isRequired,
    fsPrice: PropTypes.number.isRequired,
    onSelectPrice: PropTypes.func,
};

export default TotalBlocks;